import { useEffect, useLayoutEffect, useState } from "react";

/** Wall-clock duration of the paywall's introductory offer (seconds). */
export const PAYWALL_TIMER_DURATION_SECONDS = 60 * 10;
/** Once the offer has expired, the countdown cycles over this window. */
export const PAYWALL_TIMER_RESET_SECONDS = 60 * 5;

/**
 * Remaining seconds until `endsAt`, derived from the system clock on every
 * tick (never decremented), so a backgrounded tab reads right when it's back.
 * Past zero it cycles through `PAYWALL_TIMER_RESET_SECONDS` windows.
 */
export function useRemainingSecondsUntil(endsAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());

  useLayoutEffect(() => {
    if (endsAt != null) setNow(Date.now());
  }, [endsAt]);

  useEffect(() => {
    if (endsAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  if (endsAt == null) return 0;
  const remainingSeconds = Math.ceil((endsAt - now) / 1000);

  if (remainingSeconds > 0) return remainingSeconds;

  const elapsedSinceEnd = Math.floor((now - endsAt) / 1000);
  const cyclePosition = elapsedSinceEnd % (PAYWALL_TIMER_RESET_SECONDS + 1);
  return cyclePosition === 0
    ? 0
    : PAYWALL_TIMER_RESET_SECONDS + 1 - cyclePosition;
}
